import type { IncomeBoat } from '@sea-snag/shared';

import { boatRow, type IncomeMetric, type IncomeTotals } from './income';

// Income → Year comparison (#217): two seasons side by side, from the same
// cells as the Overview. Read-only.

/** Main − comparison, and the change in percent when the comparison is above zero. */
export function change(main: number, compare: number): { diff: number; pct: number | null } {
  const diff = main - compare;
  return { diff, pct: compare > 0 ? (diff / compare) * 100 : null };
}

export interface Commercial {
  guestBookings: number;
  /** Guest charter weeks: booked days / 7. */
  guestWeeks: number;
  guestNetCents: number;
  /** Guest net per sold week; null without guest weeks, never 0. */
  perWeekCents: number | null;
}

/**
 * Commercial performance: guest charters only, owner bookings left out of both
 * the revenue and the weeks. A fleet's rate is its total over its total weeks,
 * never an average of boats' rates.
 */
export function commercial(t: IncomeTotals): Commercial {
  const guestWeeks = t.guestDays / 7;
  return {
    guestBookings: t.guestBookings,
    guestWeeks,
    guestNetCents: t.guestNetCents,
    perWeekCents: guestWeeks > 0 ? t.guestNetCents / guestWeeks : null,
  };
}

const pct1 = (n: number) => `${Math.abs(n).toFixed(1)}%`;
const moved = (n: number) => (n > 0 ? 'rose' : 'fell');

/** "one", "2" or "1.5", then the noun, singular only for exactly one. */
function count(n: number, singular: string, plural: string): string {
  const text = n === 1 ? 'one' : Number.isInteger(n) ? String(n) : n.toFixed(1);
  return `${text} ${n === 1 ? singular : plural}`;
}

/**
 * A short, deterministic reading of the commercial change, only when both
 * seasons have guest weeks. Conditions use unrounded values. A claim that
 * extra weeks kept the revenue the same is made only for the same boats; for
 * fleets that changed it stays neutral and says the fleet changed.
 */
export function observation(
  main: Commercial,
  compare: Commercial,
  scope: { sameBoats: boolean; fleetChanged?: boolean }
): string | null {
  if (main.perWeekCents === null || compare.perWeekCents === null) return null;
  const weeks = change(main.guestWeeks, compare.guestWeeks);
  const revenue = change(main.guestNetCents, compare.guestNetCents);
  const rate = change(main.perWeekCents, compare.perWeekCents);
  const rateLine =
    rate.diff === 0
      ? 'Average net revenue per sold week was unchanged.'
      : `Average net revenue per sold week ${moved(rate.diff)} by ${pct1(rate.pct ?? 0)}.`;

  if (scope.sameBoats && revenue.diff === 0 && weeks.diff !== 0) {
    const n = Math.abs(weeks.diff);
    return weeks.diff > 0
      ? `You sold ${count(n, 'additional guest charter week', 'additional guest charter weeks')} to maintain the same revenue. ${rateLine}`
      : `You kept the same revenue with ${count(n, 'fewer guest charter week', 'fewer guest charter weeks')}. ${rateLine}`;
  }

  const weeksLine =
    weeks.diff === 0
      ? 'Guest charter weeks were unchanged'
      : `Guest charter weeks ${moved(weeks.diff)} by ${pct1(weeks.pct ?? 0)}`;
  const revenueLine =
    revenue.diff === 0
      ? 'guest net revenue was unchanged'
      : `guest net revenue ${moved(revenue.diff)} by ${pct1(revenue.pct ?? 0)}`;
  const fleet =
    !scope.sameBoats && scope.fleetChanged
      ? ' The fleet changed between the seasons, so part of the difference is the fleet’s.'
      : '';
  return `${weeksLine} and ${revenueLine}. ${rateLine}${fleet}`;
}

/** The boats present in both seasons, by their stable id; never matched by name. */
export function sameBoatsOnly(
  main: readonly IncomeBoat[],
  compare: readonly IncomeBoat[]
): [IncomeBoat[], IncomeBoat[]] {
  const inMain = new Set(main.map((b) => b.boatPid));
  const inCompare = new Set(compare.map((b) => b.boatPid));
  return [
    main.filter((b) => inCompare.has(b.boatPid)),
    compare.filter((b) => inMain.has(b.boatPid)),
  ];
}

export interface BoatComparisonRow {
  boatPid: string;
  name: string;
  /** In both seasons, or only one: "New to fleet" or "Not in selected season". */
  status: 'both' | 'newToFleet' | 'notInSelectedSeason';
  /** Null where the boat isn't in that season, or its amount isn't known. */
  main: number | null;
  compare: number | null;
  /** Only for a boat in both seasons with both amounts known. */
  diff: number | null;
  pct: number | null;
}

const metricOf = (row: ReturnType<typeof boatRow>, metric: IncomeMetric): number | null =>
  metric === 'gross'
    ? row.grossCents
    : metric === 'net'
      ? row.netCents
      : metric === 'owner'
        ? row.ownerCents
        : row.companyCents;

/** Each boat in either season, compared on one metric, by name. */
export function boatComparisonRows(
  main: readonly IncomeBoat[],
  compare: readonly IncomeBoat[],
  currency: string,
  metric: IncomeMetric
): BoatComparisonRow[] {
  const byPid = new Map<string, { name: string; main?: IncomeBoat; compare?: IncomeBoat }>();
  for (const b of main) byPid.set(b.boatPid, { name: b.name, main: b });
  for (const b of compare) {
    const entry = byPid.get(b.boatPid) ?? { name: b.name };
    entry.compare = b;
    byPid.set(b.boatPid, entry);
  }
  return [...byPid]
    .map(([boatPid, e]): BoatComparisonRow => {
      const m = e.main ? metricOf(boatRow(e.main, currency), metric) : null;
      const c = e.compare ? metricOf(boatRow(e.compare, currency), metric) : null;
      const both = e.main !== undefined && e.compare !== undefined;
      const known = both && m !== null && c !== null;
      const ch = known ? change(m, c) : null;
      return {
        boatPid,
        name: e.name,
        status: both ? 'both' : e.main ? 'newToFleet' : 'notInSelectedSeason',
        main: m,
        compare: c,
        diff: ch?.diff ?? null,
        pct: ch?.pct ?? null,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
